import { ArrowDown, ArrowUp, Languages, Plus, Trash2 } from "lucide-react";
import { useId } from "react";

import {
  movePart,
  newDraftId,
  newDraftPart,
  partKindLabel,
  removePart,
  splitPartKey,
  updatePart,
  type DraftPart,
  type PartKind,
  type PartProblem,
} from "@/lib/assetContent";
import {
  addRequiredLanguage,
  missingRequiredLanguages,
} from "@/lib/namedParts";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { Label, Input, Textarea } from "@/ui/Input";
import { useFieldAnchor } from "@/ui/validation/FieldAnchorContext";

/**
 * The ordered parts of a library asset, editable.
 *
 * Presentational and total: it owns no draft and performs no request, so the
 * same component renders the authoring surface and the read-only one. That is
 * what makes #33's "Lovable gap" structurally impossible here rather than a
 * matter of remembering to disable things — `readOnly` is one prop, and every
 * control below reads it. The prototype's defect was labelling content
 * read-only while leaving Save, Add and Remove live; there is no arrangement of
 * these props that produces that.
 *
 * ORDER IS CONTENT. `parts` is an ordered list by contract ("order is authored
 * order and is preserved"), so moving a part is an edit of the same weight as
 * retyping one, and it produces a new version exactly as any other edit does.
 *
 * Reorder is buttons rather than drag. A drag handle needs a keyboard story
 * bolted on afterwards and a dependency to do it well; two buttons are operable
 * by keyboard, pointer, and assistive technology on the first day, and the list
 * they reorder is short by construction (64 parts, and real assets carry two to
 * ten).
 */

const KINDS: PartKind[] = ["prompt_text", "structured"];

const KIND_HINT: Record<PartKind, string> = {
  prompt_text:
    "A prompt document — the instruction text an agent is given, mirroring a .txt/.md pack file.",
  structured:
    "A structured document as JSON — ordered steps, thresholds, schemas, mirroring a .yaml/.json pack file.",
};

function problemsFor(
  problems: readonly PartProblem[],
  index: number | null,
): PartProblem[] {
  return problems.filter((problem) => problem.index === index);
}

function PartRow({
  part,
  index,
  total,
  readOnly,
  problems,
  inherits,
  onChange,
  onMove,
  onRemove,
}: {
  part: DraftPart;
  index: number;
  total: number;
  readOnly: boolean;
  problems: readonly PartProblem[];
  /**
   * Whether the service publishes `structured_deep_merge` for this part's role.
   *
   * Decided by the caller off the placement registry, never inferred from the
   * kind: `structured` is what the document IS, and how it composes with base is
   * a separate published fact that only the service may state.
   */
  inherits: boolean;
  onChange: (patch: Partial<Pick<DraftPart, "key" | "text">>) => void;
  onMove: (to: number) => void;
  onRemove: () => void;
}) {
  // The seam #7 built for exactly this: the control registers itself by path,
  // and a finding that names the path gets a jump target without either side
  // knowing about the other. Positional, because `parts` is an ordered list and
  // a part's identity within a version IS its position plus its key.
  const { ref: anchorRef, id: anchorId } = useFieldAnchor(
    `content.parts[${index}]`,
  );
  const keyId = useId();
  const problemId = useId();
  const kindHintId = useId();

  const rowProblems = problems;
  const invalid = rowProblems.length > 0;
  const describedBy = [invalid ? problemId : null, kindHintId]
    .filter((id): id is string => id !== null)
    .join(" ");

  return (
    <li className="rounded-md border border-border bg-card p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1.5">
          <Label htmlFor={keyId}>Part key</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id={keyId}
              className="max-w-xs font-mono"
              value={part.key}
              readOnly={readOnly}
              aria-disabled={readOnly || undefined}
              aria-invalid={invalid || undefined}
              aria-describedby={describedBy}
              placeholder="he/screening_prompt"
              onChange={(event) => onChange({ key: event.target.value })}
            />
            {/* The kind is fixed once the part exists. It is half the contract
                with the pack file family this part becomes — a role is a prompt
                file or a structured one, never either — and flipping it in
                place would have to either reinterpret the buffer (a prompt is
                not JSON) or silently discard it. Changing a part's kind is
                removing it and adding the right one, which is visible in the
                diff the next version records. */}
            <Badge tone="outline">{partKindLabel(part.kind)}</Badge>
          </div>
          <p id={kindHintId} className="text-xs text-muted-foreground">
            {KIND_HINT[part.kind]}
          </p>
        </div>

        {readOnly ? null : (
          <div className="flex items-center gap-1">
            <IconButton
              label={`Move ${part.key || `part ${index + 1}`} up`}
              icon={<ArrowUp className="h-4 w-4" />}
              disabled={index === 0}
              onClick={() => onMove(index - 1)}
            />
            <IconButton
              label={`Move ${part.key || `part ${index + 1}`} down`}
              icon={<ArrowDown className="h-4 w-4" />}
              disabled={index === total - 1}
              onClick={() => onMove(index + 1)}
            />
            <IconButton
              label={`Remove ${part.key || `part ${index + 1}`}`}
              icon={<Trash2 className="h-4 w-4" />}
              onClick={onRemove}
            />
          </div>
        )}
      </div>

      <div className="mt-3 space-y-1.5">
        <Label htmlFor={anchorId}>
          {part.kind === "prompt_text" ? "Prompt text" : "Structured document"}
        </Label>
        {/* The anchor rides the body rather than the key: a finding about a
            part is about what the part SAYS, and focus should land where the
            clinician will type. Its id is the element's id, as
            `SectionFieldList` does it, so a finding can also point
            `aria-describedby` here. */}
        <Textarea
          id={anchorId}
          ref={anchorRef}
          rows={part.kind === "prompt_text" ? 6 : 8}
          className={part.kind === "structured" ? "font-mono" : undefined}
          value={part.text}
          readOnly={readOnly}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          spellCheck={part.kind === "structured" ? false : undefined}
          onChange={(event) => onChange({ text: event.target.value })}
        />
        {inherits ? (
          <p className="text-xs text-muted-foreground">
            {/* The inherited-base truth, stated at the control a clinician types
                into, because that is where the wrong reading forms. This
                document is an OVERLAY on the pinned base document, not the whole
                of it: the runtime reads both.

                IT ASSERTS THAT A BASE DOCUMENT EXISTS, which is why the caller
                may not hand this list every `structured_deep_merge` role.
                `inheritingRolesFor` answers only for a `fixed` family, whose
                parts land at base canon's own relpaths; a coordinate family's
                slug is authored and base canon ordinarily carries nothing
                there, so it arrives here as `[]` and no sentence is rendered.
                The surface never decides this, and it says nothing when the
                service has not said it. */}
            This document is merged into the base document rather than replacing
            it. Keys you leave out stay inherited from base, and removing one
            here does not remove it from the runtime.
          </p>
        ) : null}
      </div>

      {invalid ? (
        // The live region is the WRAPPER, not each item. A `role` on an `<li>`
        // replaces its implicit `listitem`, which leaves the `<ul>` holding
        // children that are not list items — axe's `list` rule, and a screen
        // reader that stops announcing "list of 2" with it. Announcing the group
        // once is also the better behaviour: three refusals on one part used to
        // fire three assertive interruptions.
        //
        // The `id` stays on the `<ul>`, so the `aria-describedby` this row puts
        // on both of its controls still resolves to the list it names.
        <div role="alert">
          <ul id={problemId} className="mt-2 space-y-1">
            {rowProblems.map((problem, position) => (
              // Keyed on the position, not the sentence — `NamedProblems`'s
              // rule, and load-bearing here rather than merely uniform. This
              // component never sees a `findPartProblems` array: `problems`
              // reaches it as `[...clientProblems, ...serverProblems]`, and a
              // server refusal is fanned across positions `attributePartsFailure`
              // regex-parses out of the message itself — filtered for validity,
              // never for uniqueness. A React key here may not rest on a list
              // comprehension in the service continuing not to repeat itself.
              <li
                key={`${position}:${problem.index}:${problem.message}`}
                className="text-xs text-destructive"
              >
                {problem.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </li>
  );
}

export function PartsEditor({
  parts,
  onChange,
  readOnly,
  readOnlyReason,
  problems = [],
  requiredLanguages,
  inheritingRoles,
}: {
  parts: readonly DraftPart[];
  onChange: (parts: DraftPart[]) => void;
  readOnly: boolean;
  /**
   * Why editing is refused, in the service's own words where there are any.
   *
   * Required rather than optional when `readOnly`: content shown read-only
   * without a reason is the state this ticket was written to remove.
   */
  readOnlyReason: string | null;
  problems?: readonly PartProblem[];
  /**
   * Languages the SERVICE says a version of this family may not omit (#635).
   *
   * Passed in, never derived: it is `FamilyPolicy.languages.required` off the
   * placement registry — the same projection the write path's own gate reads —
   * and a list invented here would be a second policy free to drift from the one
   * that decides. Empty is the ordinary case and the honest degraded one alike:
   * no control appears, and the server's refusal stays the authority.
   *
   * REQUIRED, not optional-with-a-default, for `readOnlyReason`'s reason and one
   * more. An `= []` default cannot be told apart from a registry that answered
   * "none", so a surface that simply forgot the prop rendered as though the
   * service had spoken: the create dialog silently offered no bilingual hint and
   * no remedy at the one place `validate_required_languages` first refuses, and
   * neither lint nor a test could see it. Required makes the omission a
   * TYPECHECK failure at every render site, including the next one.
   */
  requiredLanguages: readonly string[];
  /**
   * Roles whose authored document merges onto an EXISTING base document (#635).
   *
   * Passed in for the same reason `requiredLanguages` is, and required for the
   * same reason: it is `api/library.inheritingRolesFor` off the placement
   * registry, and a surface that decided this for itself would be publishing
   * composition semantics the engine owns. Empty says nothing rather than
   * claiming replacement — and, since that helper answers `[]` for every
   * `coordinate` family, empty is what a main concern, a pathway and a variant
   * all get.
   */
  inheritingRoles: readonly string[];
}) {
  const listProblems = problemsFor(problems, null);
  // The refusal a clinician meets on a bilingual family is
  // "<type> requires the parts for 'he': …" — attributed at LIST level, because
  // a language the asset does not carry has no control on screen to blame. This
  // is the control that gives it one: it copies every document this asset
  // already has into the missing language, so the next Save's refusal (or its
  // success) lands on parts the clinician can see and type into.
  //
  // Offered only once the asset HAS a document to copy. `addRequiredLanguage`
  // mirrors the roles the asset already carries and invents none, so on an empty
  // draft it would be a button that does nothing — the "Create button that did
  // nothing at all" Manual QA already found once on this family of surfaces.
  const missing =
    readOnly || parts.length === 0
      ? []
      : missingRequiredLanguages(parts, requiredLanguages);
  // The STATEMENT and the REMEDY come apart on the create surface, and only
  // there. A new asset opens on one part whose key is still empty, so its role
  // is `""`: `addRequiredLanguage` would mirror that role and produce `he/`, a
  // key the service refuses — the empty-draft button this list was already
  // guarded against, arriving by a second route once the create dialog began
  // passing `requiredLanguages` at all (#635). The requirement sentence below is
  // true and useful on that first frame and keeps rendering; the button waits
  // until there is a real role to copy.
  const mirrorable = parts.some((part) => splitPartKey(part.key)[1] !== "");
  // The pointer the service itself uses for every parts refusal, registered so
  // a finding that names it resolves to this list rather than to nothing.
  const { ref: listRef, id: listId } = useFieldAnchor("content.parts");

  return (
    <div className="space-y-3">
      {readOnly && readOnlyReason ? (
        <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
          {readOnlyReason}
        </p>
      ) : null}

      <ol
        ref={listRef}
        id={listId}
        tabIndex={-1}
        aria-label="Asset parts, in authored order"
        className="space-y-3"
      >
        {parts.map((part, index) => (
          <PartRow
            key={part.id}
            part={part}
            index={index}
            total={parts.length}
            readOnly={readOnly}
            problems={problemsFor(problems, index)}
            inherits={inheritingRoles.includes(splitPartKey(part.key)[1])}
            onChange={(patch) => onChange(updatePart(parts, index, patch))}
            onMove={(to) => onChange(movePart(parts, index, to))}
            onRemove={() => onChange(removePart(parts, index))}
          />
        ))}
      </ol>

      {listProblems.length > 0 ? (
        // One live region for the group, as in `PartRow` above and for the same
        // reason: a `role` on an `<li>` replaces its implicit `listitem` and
        // leaves the `<ul>` holding children that are not list items.
        <div role="alert">
          <ul className="space-y-1">
            {listProblems.map((problem, position) => (
              // Position-keyed, as in `PartRow` above and for the same reason:
              // this list is assembled by the caller, not by `findPartProblems`,
              // so nothing at this render site makes the attribution plus the
              // sentence unique.
              <li
                key={`${position}:${problem.index}:${problem.message}`}
                className="text-sm text-destructive"
              >
                {problem.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {readOnly ? null : (
        <div className="flex flex-wrap gap-2">
          {KINDS.map((kind) => (
            <Button
              key={kind}
              variant="outline"
              size="sm"
              onClick={() => onChange([...parts, newDraftPart(kind)])}
            >
              <Plus aria-hidden="true" className="h-4 w-4" />
              Add {partKindLabel(kind).toLowerCase()} part
            </Button>
          ))}
          {(mirrorable ? missing : []).map((language) => (
            <Button
              key={language}
              variant="outline"
              size="sm"
              onClick={() =>
                onChange(addRequiredLanguage(parts, language, newDraftId))
              }
            >
              <Languages aria-hidden="true" className="h-4 w-4" />
              Add {language} documents
            </Button>
          ))}
        </div>
      )}
      {missing.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          {/* The requirement, in the registry's own vocabulary and before the
              Save that would otherwise report it. Not a client-side refusal:
              nothing here blocks Save, because the service decides and this
              surface must never refuse content the server accepts. */}
          This asset type requires {requiredLanguages.join(" and ")}. Missing:{" "}
          {missing.join(", ")}.
        </p>
      ) : null}
    </div>
  );
}
